import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Muted } from '../Controls';
import { Panel, PanelTitle } from '../Page';
import { imageUrl } from '../services/helpers';
import { matchingSections } from './matchingSections';
import { sectionForTopic } from './book';
import { useOpenImage } from './viewerContext';
import { Body, Contents, Pages, PartTitle, Search } from './styled';

export const sectionAnchor = (section) => `rule-${section.position}`;

/** The server writes pictures as /image/… paths; the API's images may live on another host, as cards' do. */
const withImageHost = (html) => html.replace(/src="\/image\//g, `src="${imageUrl('/')}`);

const pagesLabel = ({ first_page: first, last_page: last }) => (first === last ? `Page ${first}` : `Pages ${first}–${last}`);

/** The rulebook's text, searchable, with the Expert Rules set apart as in the book. */
const RulesText = ({ sections, loaded }) => {
  const [search, setSearch] = useState('');
  const { hash } = useLocation();
  const navigate = useNavigate();

  // The index's page numbers link to #page-N: go to the text printed on that page. Navigating sets the hash, so
  // the effect below scrolls there and Back returns to the index.
  const openImage = useOpenImage();

  const onBodyClick = (event) => {
    // Pictures open full screen, except the little portrait beside each tip and the energy symbols in the text.
    const isPicture = event.target.tagName === 'IMG' && !event.target.src.endsWith('#energy');
    if (isPicture && !event.target.closest('blockquote')) {
      openImage(event.target.src, event.target.alt);
      return;
    }
    const link = event.target.closest('a[href^="#page-"]');
    const topic = link && link.closest('tr').querySelector('td').textContent.trim();
    const section = link && sectionForTopic(sections, Number(link.getAttribute('href').slice('#page-'.length)), topic);
    if (section) {
      event.preventDefault();
      setSearch('');
      if (hash === `#${sectionAnchor(section)}`) {
        document.getElementById(sectionAnchor(section)).scrollIntoView();
      } else {
        navigate({ hash: sectionAnchor(section) });
      }
    }
  };

  // Arriving with #rule-N (from the booklet, or the index): jump to that section once it has rendered.
  useEffect(() => {
    if (loaded && hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView();
      }
    }
  }, [loaded, hash]);

  const shown = matchingSections(sections, search);
  const parts = [
    { title: null, sections: shown.filter(section => !section.expert) },
    { title: 'Expert Rules', sections: shown.filter(section => section.expert) },
  ];

  return (
    <>
      <Search
        type="search"
        placeholder="Search the rules"
        aria-label="Search the rules"
        value={search}
        onChange={event => setSearch(event.target.value)}
      />

      {loaded && !shown.length && <Muted>No rules match “{search}”.</Muted>}

      {!search && shown.length > 0 && (
        <Panel>
          <PanelTitle>Contents</PanelTitle>
          <Contents>
            {shown.map(section => (
              <li key={section.id}><a href={`#${sectionAnchor(section)}`}>{section.heading}</a></li>
            ))}
          </Contents>
        </Panel>
      )}

      {parts.map(part => part.sections.length > 0 && (
        <React.Fragment key={part.title || 'rules'}>
          {part.title && <PartTitle>{part.title}</PartTitle>}
          {part.sections.map(section => (
            <Panel key={section.id} id={sectionAnchor(section)}>
              <PanelTitle as="h3">
                {section.heading}
                <Pages>
                  {pagesLabel(section)} · <Link to={`/rules?page=${section.first_page}`}>See it in the book</Link>
                </Pages>
              </PanelTitle>
              {/* The server renders the transcription with raw HTML escaped. */}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- delegates clicks on the links inside */}
              <Body onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: withImageHost(section.html) }} />
            </Panel>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

RulesText.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  loaded: PropTypes.bool.isRequired,
};

export default RulesText;
